import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Save, Loader2, ExternalLink, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", category: "Guidance", excerpt: "", body: "", published: false };

function ArticleEditor({ initial, onDone, onCancel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { title: form.title, category: form.category, excerpt: form.excerpt, body: form.body, published: form.published };
      const { data } = initial.id ? await api.patch(`/admin/articles/${initial.id}`, payload) : await api.post("/admin/articles", payload);
      toast.success(initial.id ? "Article saved" : "Article created"); onDone(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-border rounded-2xl p-5 bg-card space-y-4" data-testid="admin-article-editor">
      <div className="grid sm:grid-cols-[1fr_180px] gap-3">
        <div><Label>Title</Label><Input required minLength={3} value={form.title} onChange={set("title")} className="mt-1.5" data-testid="admin-article-title-input" /></div>
        <div><Label>Category</Label><Input value={form.category} onChange={set("category")} className="mt-1.5" placeholder="CQC, Ofsted, Start-up…" data-testid="admin-article-category-input" /></div>
      </div>
      <div><Label>Short summary</Label><Input value={form.excerpt} onChange={set("excerpt")} maxLength={300} className="mt-1.5" placeholder="One or two sentences shown in the list" data-testid="admin-article-excerpt-input" /></div>
      <div>
        <Label>Body <span className="text-muted-foreground font-normal">(Markdown: ## headings, **bold**, - lists)</span></Label>
        <Textarea required minLength={20} rows={14} value={form.body} onChange={set("body")} className="mt-1.5 font-mono text-sm" data-testid="admin-article-body-input" />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Switch id="pub" checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="admin-article-published-switch" />
          <Label htmlFor="pub">Published (visible on site)</Label>
        </div>
        <div className="flex gap-2">
          {onCancel && <Button type="button" variant="ghost" onClick={onCancel} data-testid="admin-article-cancel"><X className="w-4 h-4 mr-1" /> Cancel</Button>}
          <Button type="submit" disabled={busy} data-testid="admin-article-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> {initial.id ? "Save changes" : "Create article"}</>}</Button>
        </div>
      </div>
    </form>
  );
}

export default function AdminArticles() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/admin/articles").then((r) => setItems(r.data)); }, []);

  const upsert = (d) => { setItems((l) => (l.some((x) => x.id === d.id) ? l.map((x) => (x.id === d.id ? d : x)) : [d, ...l])); setEditing(null); };
  const togglePublish = async (a) => {
    const { data } = await api.patch(`/admin/articles/${a.id}`, { published: !a.published });
    upsert(data); toast.success(data.published ? "Published" : "Unpublished");
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/admin/articles/${a.id}`); setItems((l) => l.filter((x) => x.id !== a.id)); toast.success("Deleted");
  };

  return (
    <div className="space-y-6" data-testid="admin-articles">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Guidance articles appear at <Link to="/guidance" className="text-primary hover:underline">/guidance</Link> and help Google find the site.</p>
        {!editing && <Button size="sm" onClick={() => setEditing(EMPTY)} data-testid="admin-article-new-button"><Plus className="w-4 h-4 mr-1" /> New article</Button>}
      </div>
      {editing && <ArticleEditor initial={editing} onDone={upsert} onCancel={() => setEditing(null)} />}
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="border border-border rounded-2xl p-4 bg-card flex items-start gap-4 flex-wrap sm:flex-nowrap" data-testid={`admin-article-${a.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="secondary">{a.category}</Badge>
                <Badge variant={a.published ? "default" : "outline"} data-testid={`admin-article-status-${a.id}`}>{a.published ? "Published" : "Draft"}</Badge>
                <span className="text-xs text-muted-foreground">{a.reading_minutes} min · /guidance/{a.slug}</span>
              </div>
              <p className="font-semibold truncate">{a.title}</p>
              <p className="text-sm text-muted-foreground line-clamp-1">{a.excerpt}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {a.published && <Button asChild variant="ghost" size="sm"><Link to={`/guidance/${a.slug}`} target="_blank"><ExternalLink className="w-4 h-4" /></Link></Button>}
              <Button variant="ghost" size="sm" onClick={() => togglePublish(a)} data-testid={`admin-article-toggle-${a.id}`}>{a.published ? "Unpublish" : "Publish"}</Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(a)} data-testid={`admin-article-edit-${a.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => remove(a)} data-testid={`admin-article-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
